import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const ShopContext = createContext(null);

const STORAGE_KEY = "zyncfit_shop_domain";
const TOKEN_KEY = "zyncfit_admin_token";
const API_BASE = "/api";

function getAuthHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function ShopProvider({ children }) {
  const [shopDomain, setShopDomain] = useState(() => {
    // 1. Check URL params (after OAuth redirect)
    const params = new URLSearchParams(window.location.search);
    const urlShop = params.get("shop");
    if (urlShop) {
      localStorage.setItem(STORAGE_KEY, urlShop);
      return urlShop;
    }
    // 2. Fall back to localStorage
    return localStorage.getItem(STORAGE_KEY) || null;
  });
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [shops, setShops] = useState([]);

  // Fetch all connected shops
  const fetchShops = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/connections`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) { setShops([]); return []; }
      const data = await res.json();
      setShops(data.shops || []);
      return data.shops || [];
    } catch {
      setShops([]);
      return [];
    }
  }, []);

  const checkConnection = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all shops
      const allShops = await fetchShops();

      // If we have a shop domain, check that specific one
      if (shopDomain) {
        const res = await fetch(`${API_BASE}/auth/status?shop=${encodeURIComponent(shopDomain)}`, {
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (data.connected) {
          setConnected(true);
          setConnectionInfo(data);
          localStorage.setItem(STORAGE_KEY, data.shopDomain);
          setShopDomain(data.shopDomain);
          setLoading(false);
          return;
        }
      }
      // Otherwise, use the first shop from the list
      if (allShops.length > 0) {
        const first = allShops[0];
        setConnected(true);
        setConnectionInfo({ connected: true, ...first });
        setShopDomain(first.shopDomain);
        localStorage.setItem(STORAGE_KEY, first.shopDomain);
      } else {
        setConnected(false);
        setConnectionInfo(null);
        setShopDomain(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, [shopDomain, fetchShops]);

  useEffect(() => {
    checkConnection();
    // Clean URL params after OAuth redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") || params.get("shop")) {
      params.delete("connected");
      params.delete("shop");
      const clean = params.toString();
      const newUrl = window.location.pathname + (clean ? `?${clean}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch active shop
  const switchShop = useCallback((domain) => {
    if (domain === shopDomain) return;
    setShopDomain(domain);
    localStorage.setItem(STORAGE_KEY, domain);
    const match = shops.find((s) => s.shopDomain === domain);
    if (match) {
      setConnected(true);
      setConnectionInfo({ connected: true, ...match });
    }
  }, [shopDomain, shops]);

  const disconnect = useCallback(async () => {
    if (!shopDomain) return;
    await fetch(`${API_BASE}/auth/disconnect`, {
      method: "DELETE",
      headers: { "x-shop-domain": shopDomain, ...getAuthHeaders() },
    });
    // Remove from local list
    setShops((prev) => prev.filter((s) => s.shopDomain !== shopDomain));
    // Switch to next available shop or clear
    const remaining = shops.filter((s) => s.shopDomain !== shopDomain);
    if (remaining.length > 0) {
      switchShop(remaining[0].shopDomain);
    } else {
      setConnected(false);
      setConnectionInfo(null);
      setShopDomain(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [shopDomain, shops, switchShop]);

  const apiFetch = useCallback(async (path, options = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...options.headers,
    };
    if (shopDomain) {
      headers["x-shop-domain"] = shopDomain;
    }
    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || "API error");
    }
    return res.json();
  }, [shopDomain]);

  return (
    <ShopContext.Provider value={{
      shopDomain,
      connected,
      loading,
      connectionInfo,
      shops,
      checkConnection,
      switchShop,
      disconnect,
      apiFetch,
    }}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
}
